// src/components/WellnessGauge.tsx
import React from 'react';
import styled from 'styled-components';

// --- TYPE DEFINITIONS ---
interface WellnessGaugeProps {
    score: number; // The raw score, e.g., 9
    category: string; // The category text, e.g., "High"
}

// --- STYLED COMPONENTS ---

const GaugeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 32px;
`;

const GaugeCircle = styled.div`
  width: 180px;
  height: 180px;
  border-radius: 50%;
  background: radial-gradient(circle, #FFFFFF 70%, #F8F9FA 100%);
  padding: 18px; /* This padding defines the track's thickness */
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.05);
  position: relative;
`;

const GaugeTrack = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: conic-gradient(
    from 135deg,
    #EF4444, /* Red */
    #F97316, /* Orange */
    #F59E0B, /* Yellow */
    #10B981  /* Green */
  );

  /* This mask creates the open arc effect */
  mask: radial-gradient(
    farthest-side,
    transparent calc(100% - 19px), /* Creates the inner transparent hole */
    #fff 0                       /* Makes the rest of the track visible */
  ),
  conic-gradient(from 135deg, transparent 0 90deg, #fff 91deg 360deg);
  mask-composite: intersect;
  -webkit-mask-composite: source-in;
`;

const GaugeInnerContent = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

// This wrapper rotates the thumb from the center of the gauge.
const ThumbRotationWrapper = styled.div<{ rotation: number }>`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  transform: ${({ rotation }) => `rotate(${rotation}deg)`};
  z-index: 2;
`;

const GaugeThumb = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background-color: #F97316; /* Orange fill */
  border: 4px solid #ffffff; /* White border */
  box-shadow: 0 0 10px rgba(0,0,0,0.2);
  
  /* --- FIX: The pixel value here has been adjusted for perfect centering --- */
  transform: translate(-50%, -91px); 
`;


const ScoreText = styled.p`
  margin: 0;
  font-size: 2.5rem;
  font-weight: 800;
  color: #1F2937;
  line-height: 1;
`;

const CategoryText = styled.p`
  margin: 4px 0 0 0;
  font-size: 1rem;
  font-weight: 500;
  color: #6B7280;
`;

const Title = styled.h2`
  margin: 24px 0 4px 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InfoIcon = styled.span`
  display: inline-block;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: #E5E7EB;
  color: #6B7280;
  font-size: 12px;
  font-weight: bold;
  text-align: center;
  line-height: 18px;
  cursor: pointer;
`;

const Subtitle = styled.p`
  margin: 0;
  color: #6B7280;
`;

// --- COMPONENT LOGIC ---

const WellnessGauge: React.FC<WellnessGaugeProps> = ({ score, category }) => {
    // Map the score (1-10) to a rotation angle (-135 to 135 degrees for the 270-degree arc)
    const scoreRange = 9; // 10 - 1
    const angleRange = 270;
    const rotation = ((score - 1) / scoreRange) * angleRange - 135;


    const getCategoryLabel = (category: string = ''): string => {
        const lowerCaseCat = category.toLowerCase();

        if (
            lowerCaseCat.includes('low') ||
            lowerCaseCat.includes('elevated') ||
            lowerCaseCat.includes('warning') ||
            lowerCaseCat.includes('attention')
        ) {
            return 'Need Attention';
        }

        if (
            lowerCaseCat.includes('medium') ||
            lowerCaseCat.includes('balanced') ||
            lowerCaseCat.includes('mild')
        ) {
            return 'Fair';
        }

        if (lowerCaseCat.includes('normal') || lowerCaseCat.includes('good') || lowerCaseCat.includes('high') || lowerCaseCat.includes('Very High')) {
            return 'Good';
        }

        return category; // fallback to original
    };


    return (
        <GaugeWrapper>
            <GaugeCircle>
                <GaugeTrack />
                <ThumbRotationWrapper rotation={rotation}>
                    <GaugeThumb />
                </ThumbRotationWrapper>
                <GaugeInnerContent>
                    <ScoreText>{score}/10</ScoreText>
                    <CategoryText>{getCategoryLabel(category)}</CategoryText>
                </GaugeInnerContent>
            </GaugeCircle>
            <Title>
                Wellness Score
            </Title>
            <Subtitle>The measured indicators are not intended for medical use</Subtitle>
        </GaugeWrapper>
    );
};

export default WellnessGauge;